import { useState, useEffect, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useOutletContext } from 'react-router-dom'
import { Virtuoso } from 'react-virtuoso'

import SearchFilter from '@App/components/SearchFilter/SearchFilter'
import Dropdown from '@App/components/Dropdown/Dropdown'
import { setClickedItem } from '@App/pages/MosquitoObservations/mosquito-observations.slice'

import MosquitoObservationTabs from '../MosquitoObservationTabs/MosquitoObservationTabs'

import MosquitoObservationsSightingsListItem from './MosquitoObservationsSightingsListItem/MosquitoObservationsSightingsListItem'
import { getCertaintyPriority } from './MosquitoObservationsSightingsListItem/mosquito-observations-sightings-list-item.utils'

import './mosquito-observations-sightings-list.styl'

function getSightingId(sighting) {
  return sighting?.id
}

function getSightingSource(sighting) {
  return sighting?.properties?.source || 'unknown'
}

function getSightingCreationTime(sighting) {
  return sighting?.properties?.creationTime
}

function MosquitoObservationsSightingsList() {
  const dispatch = useDispatch()
  const { map, sightings = [], isLoading } = useOutletContext()
  const [squitoSearchValue, setSquitoSearchValue] = useState('')
  const [squitoSortValue, setSquitoSortValue] = useState(null)
  const clickedItem = useSelector(state => state.mosquitoDetection.clickedItem)
  const sightingsListRef = useRef(null)

  const squitoSortValues = [
    { id: 'name', label: 'Name' },
    { id: 'date', label: 'Date' },
    { id: 'expert', label: 'Expert validation' },
  ]

  const filteredAndSortedSightings = useMemo(() => {
    const filtered = squitoSearchValue
      ? sightings.filter((sighting) => {
          return sighting?.properties?.identification?.name?.toLowerCase()
            .includes(squitoSearchValue.toLowerCase())
        })
      : sightings

    return [...filtered].sort((a, b) => {
      if (squitoSortValue?.id === 'name') {
        const nameA = a.properties?.identification?.name ?? 'UNKNOWN'
        const nameB = b.properties?.identification?.name ?? 'UNKNOWN'
        return nameA.localeCompare(nameB)
      }
      else if (squitoSortValue?.id === 'date') {
        return new Date(getSightingCreationTime(b)) - new Date(getSightingCreationTime(a))
      }
      else if (squitoSortValue?.id === 'expert') {
        const priorityA = getCertaintyPriority(a.properties?.identification?.confidenceLabel)
        const priorityB = getCertaintyPriority(b.properties?.identification?.confidenceLabel)
        return priorityB - priorityA
      }
      return 0
    })
  }, [sightings, squitoSearchValue, squitoSortValue])

  useEffect(() => {
    if (!clickedItem) return
    const clickedIndex = filteredAndSortedSightings.findIndex(
      mosquito => getSightingId(mosquito) === getSightingId(clickedItem) && getSightingSource(mosquito) === getSightingSource(clickedItem),
    )

    if (clickedIndex >= 0 && sightingsListRef.current) {
      sightingsListRef.current.scrollToIndex({
        index: clickedIndex,
        align: 'center',
        behavior: filteredAndSortedSightings.length < 20 ? 'smooth' : 'auto',
      })
      dispatch(setClickedItem(null))
    }
  }, [clickedItem, dispatch, filteredAndSortedSightings])

  return (
    <>
      <MosquitoObservationTabs />
      <hr />
      <div className="mosquito-observations-sightings-list">
        <div className="mosquito-observations-sightings-list__head">
          <div className="mosquito-observations-sightings-list__head__filter">
            <SearchFilter
              value={squitoSearchValue}
              setValue={setSquitoSearchValue}
            />
          </div>
          <div className="mosquito-observations-sightings-list__head__content">
            <Dropdown
              className="mosquito-observations-sightings-list__head__content__sort"
              searchOptions={squitoSortValues}
              value={squitoSortValue}
              setValue={setSquitoSortValue}
            />
          </div>
        </div>
        {!isLoading && filteredAndSortedSightings.length !== 0
          ? (
              <div className="mosquito-observations-sightings-list__content">
                <Virtuoso
                  ref={sightingsListRef}
                  className="mosquito-observations-sightings-list__virtualized"
                  data={filteredAndSortedSightings}
                  increaseViewportBy={400}
                  computeItemKey={(index, mosquito) => `${getSightingSource(mosquito)}-${getSightingId(mosquito) || index}`}
                  itemContent={(_index, mosquito) => (
                    <div className="mosquito-observations-sightings-list__row">
                      <MosquitoObservationsSightingsListItem
                        map={map}
                        specimen={mosquito}
                      />
                    </div>
                  )}
                />
              </div>
            )
          : (
              <p className="mosquito-observations-sightings-list__no-data-message">
                {isLoading ? 'Loading mosquito sightings...' : 'No mosquito sightings found for the selected filters.'}
              </p>
            )}
      </div>
    </>
  )
}

export default MosquitoObservationsSightingsList
